import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Alert, CircularProgress } from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import { SplitAuthLayout, AmberButton } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyEmail, resendVerificationEmail, isEmailVerified } = useContext(AuthContext);

  const email = location.state?.email || '';
  const [resendStatus, setResendStatus] = useState({ loading: false, success: false, error: '' });
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(isEmailVerified);
  const [verificationError, setVerificationError] = useState('');
  const [pendingToken, setPendingToken] = useState(null);

  // Extract token from URL but do NOT auto-call the API — store it and wait for explicit user action.
  // Auto-calling here would consume the one-time token if Chrome speculatively prerenders the page.
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    if (token && !verified) {
      setPendingToken(token);
      navigate('/verify-email', { replace: true });
    }
  }, [location.search, navigate, verified]);

  const verifyEmailWithToken = useCallback(async (token) => {
    if (verified || verifying) return;
    setVerifying(true);
    setVerificationError('');
    try {
      await verifyEmail(token);
      setVerified(true);
      setPendingToken(null);
    } catch (err) {
      setVerificationError(err.message || 'Invalid or expired verification token. Please request a new verification email.');
    } finally {
      setVerifying(false);
    }
  }, [verifyEmail, verified, verifying]);

  const handleResend = async () => {
    setResendStatus({ loading: true, success: false, error: '' });
    try {
      await resendVerificationEmail();
      setResendStatus({ loading: false, success: true, error: '' });
    } catch (err) {
      setResendStatus({ loading: false, success: false, error: err.message || 'Failed to resend. Please try again.' });
    }
  };

  if (verifying) {
    return (
      <SplitAuthLayout statusLine="// verifying email…">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <CircularProgress size={20} sx={{ color: '#2dd4bf' }} />
          <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd' }}>Verifying your email…</Box>
        </Box>
      </SplitAuthLayout>
    );
  }

  if (pendingToken && !verified && !verificationError) {
    return (
      <SplitAuthLayout statusLine="// confirm verification">
        <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(45,212,191,.14)', border: '1px solid rgba(45,212,191,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', fontSize: '22px', color: '#2dd4bf' }}>
          ✉
        </Box>
        <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
          Confirm your email
        </Box>
        <Box sx={{ mb: '28px', fontSize: '14.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
          Click the button below to verify your email address and activate your account.
        </Box>
        <AmberButton onClick={() => verifyEmailWithToken(pendingToken)} size="lg" fullWidth>
          Verify email address <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
        </AmberButton>
      </SplitAuthLayout>
    );
  }

  if (verificationError) {
    return (
      <SplitAuthLayout statusLine="// verification failed">
        <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(244,113,106,.14)', border: '1px solid rgba(244,113,106,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', fontSize: '22px', color: '#f4716a' }}>
          ✕
        </Box>
        <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
          Verification failed
        </Box>
        <Alert severity="error" sx={{ mb: '24px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
          {verificationError}
        </Alert>
        {pendingToken && (
          <AmberButton onClick={() => { setVerificationError(''); verifyEmailWithToken(pendingToken); }} size="lg" fullWidth sx={{ mb: '12px' }}>
            Try again <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
          </AmberButton>
        )}
        <AmberButton onClick={handleResend} size="lg" fullWidth disabled={resendStatus.loading} sx={{ ...(pendingToken ? { background: 'transparent', border: '1px solid rgba(120,150,200,.28)', color: '#a9bcdc', '&:hover': { background: 'transparent', borderColor: 'rgba(45,212,191,.5)', color: '#fff' } } : {}) }}>
          {resendStatus.loading ? <CircularProgress size={18} sx={{ color: '#241200' }} /> : 'Resend verification email'}
        </AmberButton>
      </SplitAuthLayout>
    );
  }

  if (verified) {
    return (
      <SplitAuthLayout statusLine="// email verified ✓">
        <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(68,208,123,.14)', border: '1px solid rgba(68,208,123,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', fontSize: '22px', color: '#44d07b' }}>
          ✓
        </Box>
        <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
          Email verified
        </Box>
        <Box sx={{ mb: '28px', fontSize: '14.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
          Your email address has been confirmed. Continue to choose your plan.
        </Box>
        <AmberButton to="/pricing" size="lg" fullWidth>
          Continue to pricing <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
        </AmberButton>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout statusLine="// check your inbox">
      <Box sx={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(45,212,191,.14)', border: '1px solid rgba(45,212,191,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', fontSize: '22px', color: '#2dd4bf' }}>
        ✉
      </Box>
      <Box component="h1" sx={{ m: 0, mb: '12px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
        Verify your email
      </Box>
      <Box sx={{ mb: '6px', fontSize: '14.5px', color: '#8fa3c6', lineHeight: 1.6 }}>
        We've sent a verification link to
      </Box>
      <Box sx={{ mb: '24px', fontFamily: MONO, fontSize: '14px', color: '#dce6fa' }}>
        {email || 'your email address'}
      </Box>
      <Box sx={{ mb: '28px', fontSize: '14px', color: '#8fa3c6', lineHeight: 1.6 }}>
        Click the link in that email to complete your registration. Check your spam folder if it doesn't arrive.
      </Box>

      {resendStatus.success && (
        <Alert severity="success" sx={{ mb: '16px', background: 'rgba(68,208,123,.1)', color: '#7fe0a6', border: '1px solid rgba(68,208,123,.3)', '& .MuiAlert-icon': { color: '#44d07b' } }}>
          Verification email resent.
        </Alert>
      )}
      {resendStatus.error && (
        <Alert severity="error" sx={{ mb: '16px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
          {resendStatus.error}
        </Alert>
      )}

      <Box
        component="button"
        onClick={handleResend}
        disabled={resendStatus.loading || resendStatus.success}
        sx={{ background: 'transparent', border: '1px solid rgba(120,150,200,.28)', color: '#a9bcdc', fontWeight: 500, fontSize: '14px', px: '16px', py: '10px', borderRadius: '9px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", width: '100%', mb: '16px', transition: 'border-color .15s, color .15s', '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' }, '&:disabled': { opacity: .5, cursor: 'default' } }}
      >
        {resendStatus.loading ? 'Sending…' : 'Resend verification email'}
      </Box>

      <Box sx={{ textAlign: 'center', fontSize: '13px', color: '#5f7398' }}>
        <Box
          component="button"
          onClick={() => navigate('/login')}
          sx={{ background: 'none', border: 'none', color: '#2dd4bf', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", fontSize: '13px', '&:hover': { color: '#5fe6d6' } }}
        >
          Back to Log in
        </Box>
      </Box>
    </SplitAuthLayout>
  );
};

export default VerifyEmail;
